/**
 * Pilotage layer — draws clearing bearings: the boundary line (free end, through
 * the object, a touch past it), a soft tinted band with ticks on the DANGER side
 * (the safe water is left clear),
 * a draggable free-end handle, a small dot on the anchored object, and the
 * bearing label at the line's midpoint. A tripped clearing (vessel on the unsafe
 * side) recolours to the alarm red. Built the same way as the route layer.
 */
import type { Map as MaplibreMap } from 'maplibre-gl';
import type { FeatureCollection } from 'geojson';
import { formatBearing, rhumbDestination } from '@/nav';
import {
  clearingFreeEnd,
  transitDrawLine,
  transitExtendedEnd,
  transitBearingDeg,
  headingDrawLine,
  headingForwardEnd,
  headingArrowDeg,
  headingLabelBearingDeg,
  type ClearingBearing,
  type Transit,
  type HeadingBearing,
} from '@/pilotage';
import { getPalette } from './style/palettes';
import type { Palette } from '@/store';

const SOURCE_ID = 'pilotage';
const LABEL_SOURCE_ID = 'pilotage-labels';

function mid(a: [number, number], b: [number, number]): [number, number] {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

/** Two short barb segments forming a chevron at `tip`, pointing along `alongDeg`. */
function arrowBarbs(tip: [number, number], alongDeg: number, sizeNm = 0.09): [number, number][][] {
  const back = (alongDeg + 180) % 360;
  return [
    [tip, rhumbDestination(tip, back + 28, sizeNm)],
    [tip, rhumbDestination(tip, back - 28, sizeNm)],
  ];
}

function build(
  clearings: ClearingBearing[],
  transits: Transit[],
  headings: HeadingBearing[],
  activeClearingId: string | null,
  activeTransitId: string | null,
  activeHeadingId: string | null,
  trippedIds: Set<string>,
): { features: FeatureCollection; labels: FeatureCollection } {
  const features: GeoJSON.Feature[] = [];
  const labels: GeoJSON.Feature[] = [];

  for (const cb of clearings) {
    const free = clearingFreeEnd(cb);
    const tripped = trippedIds.has(cb.id);
    const active = cb.id === activeClearingId;

    // A clearing bearing is a single-sided line of position FROM the object: it
    // runs from the free-end handle on the vessel side in to the object, and stops
    // there. We used to project a short stub out the far side too, which read as a
    // line THROUGH the buoy — wrong, so the drawing now ends at the anchor.

    // Danger-side soft tint: the band sits on the UNSAFE side (the opposite
    // perpendicular to the safe side), so the safe water is left clear and the
    // side to avoid is gently flagged.
    const dangerAz = cb.safeSide === 'left' ? cb.bearingDeg + 90 : cb.bearingDeg - 90;
    const bandNm = 0.3;
    const freeOff = rhumbDestination(free, dangerAz, bandNm);
    const anchorOff = rhumbDestination(cb.anchor, dangerAz, bandNm);
    features.push({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [[free, cb.anchor, anchorOff, freeOff, free]] },
      properties: { kind: 'cl-danger', id: cb.id },
    });

    // Short ticks pointing to the danger side, so the side to avoid reads even
    // where the tint is faint.
    for (let t = 0.25; t <= 0.75; t += 0.25) {
      const base: [number, number] = [
        free[0] + (cb.anchor[0] - free[0]) * t,
        free[1] + (cb.anchor[1] - free[1]) * t,
      ];
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: [base, rhumbDestination(base, dangerAz, 0.08)] },
        properties: { kind: 'cl-tick', id: cb.id },
      });
    }

    // The boundary line itself: vessel-side handle in to the object, one side only.
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [free, cb.anchor] },
      properties: { kind: 'cl-line', id: cb.id, tripped, active },
    });

    // The anchored object and the draggable free end.
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: cb.anchor },
      properties: { kind: 'cl-anchor', id: cb.id },
    });
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: free },
      properties: { kind: 'cl-handle', id: cb.id },
    });

    labels.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: mid(free, cb.anchor) },
      properties: { text: formatBearing(cb.bearingDeg), tripped },
    });
  }

  for (const t of transits) {
    const activeT = t.id === activeTransitId;
    // Directional leading line: back (a) -> front (b) -> on out past the front.
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: transitDrawLine(t) },
      properties: { kind: 'tr-line', id: t.id, active: activeT },
    });
    // Back and front marks; the front is flagged so it can read a touch larger.
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: t.a },
      properties: { kind: 'tr-mark', id: t.id, front: false },
    });
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: t.b },
      properties: { kind: 'tr-mark', id: t.id, front: true },
    });
    // Draggable handle at the extended end, to lengthen or shorten the run.
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: transitExtendedEnd(t) },
      properties: { kind: 'tr-handle', id: t.id },
    });
    labels.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: mid(t.a, t.b) },
      properties: { text: formatBearing(transitBearingDeg(t)), tripped: false },
    });
  }

  for (const hb of headings) {
    const active = hb.id === activeHeadingId;
    const line = headingDrawLine(hb);
    const fwd = headingForwardEnd(hb);
    // The bearing line itself.
    features.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: line },
      properties: { kind: 'hb-line', id: hb.id, active, back: hb.kind === 'back' },
    });
    // Direction-of-travel arrow at the forward end (points along the course).
    for (const barb of arrowBarbs(fwd, headingArrowDeg(hb))) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: barb },
        properties: { kind: 'hb-arrow', id: hb.id, back: hb.kind === 'back' },
      });
    }
    // The sighted object (fixed) and the draggable vessel-side handle.
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: hb.anchor },
      properties: { kind: 'hb-anchor', id: hb.id },
    });
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: hb.from },
      properties: { kind: 'hb-handle', id: hb.id },
    });
    // Label at the object, the bearing you sight (suffixed to read heading vs back).
    const suffix = hb.kind === 'back' ? ' astern' : '';
    labels.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: hb.anchor },
      properties: { text: `${formatBearing(headingLabelBearingDeg(hb))}${suffix}`, tripped: false },
    });
  }

  return {
    features: { type: 'FeatureCollection', features },
    labels: { type: 'FeatureCollection', features: labels },
  };
}

/** Every pilotage-tools layer, bottom to top, for creation and re-raising. */
const PILOTAGE_LAYER_IDS = [
  'pilotage-danger',
  'pilotage-tick',
  'pilotage-line',
  'pilotage-anchor',
  'pilotage-handle',
  'pilotage-transit-line',
  'pilotage-transit-mark',
  'pilotage-transit-handle',
  'pilotage-heading-line',
  'pilotage-heading-arrow',
  'pilotage-heading-anchor',
  'pilotage-heading-handle',
  'pilotage-label',
] as const;

/**
 * Lift the pilotage tools back above the chart, the same medicine as the boat
 * and the anchor ring: an on-demand vector cell appends its opaque fills above
 * everything, and a freshly plotted transit that never appears reads as a
 * plotting failure rather than a stacking one (fb-msn947se-3, plotted over the
 * NOAA cells at Ponce).
 */
export function raisePilotageLayers(map: MaplibreMap): void {
  for (const id of PILOTAGE_LAYER_IDS) {
    if (map.getLayer(id)) map.moveLayer(id);
  }
}

export function ensurePilotageLayer(
  map: MaplibreMap,
  clearings: ClearingBearing[],
  transits: Transit[],
  headings: HeadingBearing[],
  activeClearingId: string | null,
  activeTransitId: string | null,
  activeHeadingId: string | null,
  trippedIds: Set<string>,
  palette: Palette,
): void {
  const p = getPalette(palette);
  const { features, labels } = build(
    clearings,
    transits,
    headings,
    activeClearingId,
    activeTransitId,
    activeHeadingId,
    trippedIds,
  );

  if (!map.getSource(SOURCE_ID)) map.addSource(SOURCE_ID, { type: 'geojson', data: features });
  else (map.getSource(SOURCE_ID) as maplibregl.GeoJSONSource).setData(features);
  if (!map.getSource(LABEL_SOURCE_ID)) map.addSource(LABEL_SOURCE_ID, { type: 'geojson', data: labels });
  else (map.getSource(LABEL_SOURCE_ID) as maplibregl.GeoJSONSource).setData(labels);

  if (!map.getLayer('pilotage-danger')) {
    map.addLayer({
      id: 'pilotage-danger',
      type: 'fill',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'cl-danger'],
      // Soft red tint, kept light so the safe water stays clean.
      paint: { 'fill-color': p.CHBLK_RED, 'fill-opacity': 0.1 },
    });
  }
  if (!map.getLayer('pilotage-tick')) {
    map.addLayer({
      id: 'pilotage-tick',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'cl-tick'],
      paint: { 'line-color': p.CHBLK_RED, 'line-width': 1.2, 'line-opacity': 0.55 },
    });
  }
  if (!map.getLayer('pilotage-line')) {
    map.addLayer({
      id: 'pilotage-line',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'cl-line'],
      paint: {
        'line-color': ['case', ['get', 'tripped'], p.CHBLK_RED, p.UI_ACCENT],
        'line-width': ['case', ['get', 'active'], 3, 2.2],
      },
    });
  }
  if (!map.getLayer('pilotage-anchor')) {
    map.addLayer({
      id: 'pilotage-anchor',
      type: 'circle',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'cl-anchor'],
      paint: { 'circle-radius': 4, 'circle-color': p.UI_ACCENT, 'circle-stroke-color': p.UI_BG, 'circle-stroke-width': 1.5 },
    });
  }
  if (!map.getLayer('pilotage-handle')) {
    map.addLayer({
      id: 'pilotage-handle',
      type: 'circle',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'cl-handle'],
      paint: { 'circle-radius': 8, 'circle-color': p.UI_ACCENT, 'circle-stroke-color': p.UI_BG, 'circle-stroke-width': 2 },
    });
  }
  if (!map.getLayer('pilotage-transit-line')) {
    map.addLayer({
      id: 'pilotage-transit-line',
      type: 'line',
      source: SOURCE_ID,
      // Dashed leading line, to read distinctly from a clearing bearing.
      paint: {
        'line-color': p.UI_ACCENT,
        'line-width': ['case', ['get', 'active'], 3, 2],
        'line-dasharray': [3, 2],
      },
    });
  }
  if (!map.getLayer('pilotage-transit-mark')) {
    map.addLayer({
      id: 'pilotage-transit-mark',
      type: 'circle',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'tr-mark'],
      paint: {
        'circle-radius': ['case', ['get', 'front'], 6, 5],
        'circle-color': p.UI_BG,
        'circle-stroke-color': p.UI_ACCENT,
        'circle-stroke-width': 2,
      },
    });
  }
  if (!map.getLayer('pilotage-transit-handle')) {
    map.addLayer({
      id: 'pilotage-transit-handle',
      type: 'circle',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'tr-handle'],
      paint: {
        'circle-radius': 8,
        'circle-color': p.UI_ACCENT,
        'circle-stroke-color': p.UI_BG,
        'circle-stroke-width': 2,
        'circle-opacity': 0.9,
      },
    });
  }
  if (!map.getLayer('pilotage-heading-line')) {
    map.addLayer({
      id: 'pilotage-heading-line',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'hb-line'],
      // Solid, to read distinctly from the dashed transit leading line.
      paint: {
        'line-color': p.UI_ACCENT,
        'line-width': ['case', ['get', 'active'], 3, 2],
      },
    });
  }
  if (!map.getLayer('pilotage-heading-arrow')) {
    map.addLayer({
      id: 'pilotage-heading-arrow',
      type: 'line',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'hb-arrow'],
      paint: { 'line-color': p.UI_ACCENT, 'line-width': 2 },
    });
  }
  if (!map.getLayer('pilotage-heading-anchor')) {
    map.addLayer({
      id: 'pilotage-heading-anchor',
      type: 'circle',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'hb-anchor'],
      paint: {
        'circle-radius': 5,
        'circle-color': p.UI_BG,
        'circle-stroke-color': p.UI_ACCENT,
        'circle-stroke-width': 2,
      },
    });
  }
  if (!map.getLayer('pilotage-heading-handle')) {
    map.addLayer({
      id: 'pilotage-heading-handle',
      type: 'circle',
      source: SOURCE_ID,
      filter: ['==', ['get', 'kind'], 'hb-handle'],
      paint: {
        'circle-radius': 8,
        'circle-color': p.UI_ACCENT,
        'circle-stroke-color': p.UI_BG,
        'circle-stroke-width': 2,
        'circle-opacity': 0.9,
      },
    });
  }
  if (!map.getLayer('pilotage-label')) {
    map.addLayer({
      id: 'pilotage-label',
      type: 'symbol',
      source: LABEL_SOURCE_ID,
      layout: {
        'text-field': ['get', 'text'],
        'text-font': ['Open Sans Regular'],
        'text-size': 12,
        'text-offset': [0, -0.8],
        'text-anchor': 'bottom',
        'text-allow-overlap': true,
      },
      paint: {
        'text-color': ['case', ['get', 'tripped'], p.CHBLK_RED, p.UI_FG],
        'text-halo-color': p.UI_BG,
        'text-halo-width': 1.6,
      },
    });
  }
}

export function updatePilotageLayer(
  map: MaplibreMap,
  clearings: ClearingBearing[],
  transits: Transit[],
  headings: HeadingBearing[],
  activeClearingId: string | null,
  activeTransitId: string | null,
  activeHeadingId: string | null,
  trippedIds: Set<string>,
): void {
  const { features, labels } = build(
    clearings,
    transits,
    headings,
    activeClearingId,
    activeTransitId,
    activeHeadingId,
    trippedIds,
  );
  const src = map.getSource(SOURCE_ID);
  const lsrc = map.getSource(LABEL_SOURCE_ID);
  if (src) (src as maplibregl.GeoJSONSource).setData(features);
  if (lsrc) (lsrc as maplibregl.GeoJSONSource).setData(labels);
}
